import React, { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, Zap, Loader2, AlertCircle, Calendar, FileText, Activity, Tag } from 'lucide-react';
import { SEVERITY_HEX_COLORS } from '../../constants/eventStyles';
import { useGetDocumentEventsQuery, useGetSiteEventQuery } from '../../redux/api/eventsApi';
import type { SiteEvent, SiteEventDetail, EventType, EventStatus } from '../../redux/api/eventsApi';
import { formatDateTime } from '../../utils/dateUtils';

interface EventExtractionPanelProps {
  documentId: string;
  siteId: string;
  onEventClick?: (eventId: string) => void;
}

const EVENT_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  outage: { label: 'Outage', color: '#ef4444' },
  maintenance: { label: 'Maintenance', color: '#3b82f6' },
  inspection: { label: 'Inspection', color: '#8b5cf6' },
  incident: { label: 'Incident', color: '#f97316' },
  weather: { label: 'Weather', color: '#06b6d4' },
  warranty: { label: 'Warranty', color: '#ec4899' },
  pest: { label: 'Pest', color: '#84cc16' },
  vegetation: { label: 'Vegetation', color: '#22c55e' },
  communication: { label: 'Communication', color: '#6366f1' },
  regulatory: { label: 'Regulatory', color: '#f59e0b' },
  other: { label: 'Other', color: '#9ca3af' },
};

const STATUS_STYLES: Record<EventStatus, { bg: string; text: string }> = {
  open: { bg: 'rgba(245, 158, 11, 0.15)', text: '#f59e0b' },
  acknowledged: { bg: 'rgba(139, 92, 246, 0.15)', text: '#8b5cf6' },
  in_progress: { bg: 'rgba(59, 130, 246, 0.15)', text: '#3b82f6' },
  resolved: { bg: 'rgba(34, 197, 94, 0.15)', text: '#22c55e' },
  closed: { bg: 'rgba(156, 163, 175, 0.15)', text: '#9ca3af' },
};

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month ago';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

function formatStatus(status: EventStatus): string {
  return status.replace('_', ' ');
}

// --- Inline Event Detail ---

const EventInlineDetail: React.FC<{
  event: SiteEvent;
  siteId: string;
  onViewInPdf?: (eventId: string) => void;
}> = ({ event, siteId, onViewInPdf }) => {
  // Fetch full event detail (includes meta with source_excerpt/source_page)
  const { data: detail, isLoading: detailLoading } = useGetSiteEventQuery(
    { siteId, eventId: event.id },
    { skip: !siteId }
  );

  const ev = detail || event;
  const sevColor = SEVERITY_HEX_COLORS[ev.severity];
  const statusStyle = STATUS_STYLES[ev.status] || STATUS_STYLES.open;
  const meta = ev.meta;

  return (
    <div
      className="mx-2 mb-2 rounded-lg border overflow-hidden"
      style={{
        borderColor: 'var(--color-border)',
        backgroundColor: 'var(--color-bg-tertiary)',
      }}
    >
      {/* Severity accent bar */}
      <div className="h-0.5" style={{ backgroundColor: sevColor }} />

      <div className="p-3 space-y-3">
        {detailLoading && (
          <div className="flex items-center gap-2 py-1">
            <Loader2 className="w-3 h-3 text-engramiq-green animate-spin" />
            <span className="text-[10px] eg-text-muted">Loading details...</span>
          </div>
        )}

        {/* Description */}
        {ev.description && (
          <p className="text-xs eg-text-secondary leading-relaxed">
            {ev.description}
          </p>
        )}

        {/* Source excerpt quote */}
        {meta?.source_excerpt && (
          <div
            className="rounded-md p-2.5 border"
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-border)',
            }}
          >
            {meta.source_page != null && (
              <p className="text-[9px] font-medium eg-text-secondary mb-1">
                Page {meta.source_page}
              </p>
            )}
            <blockquote
              className="text-[11px] eg-text-muted leading-relaxed italic border-l-2 pl-2 line-clamp-4"
              style={{ borderLeftColor: '#17c480' }}
            >
              &ldquo;{meta.source_excerpt}&rdquo;
            </blockquote>
          </div>
        )}

        {/* Meta grid */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
            <div>
              <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Occurred</p>
              <p className="text-[11px] eg-text-primary">{formatDateTime(ev.occurred_at)}</p>
            </div>
          </div>
          {ev.resolved_at && (
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--color-text-muted)' }} />
              <div>
                <p className="text-[9px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>Resolved</p>
                <p className="text-[11px] eg-text-primary">{formatDateTime(ev.resolved_at)}</p>
              </div>
            </div>
          )}
        </div>

        {/* Badges row */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${sevColor}20`, color: sevColor }}
          >
            {ev.severity}
          </span>
          <span
            className="text-[9px] font-medium px-1.5 py-0.5 rounded capitalize"
            style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
          >
            {formatStatus(ev.status)}
          </span>
          {ev.event_category && (
            <span
              className="text-[9px] font-medium px-1.5 py-0.5 rounded"
              style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}
            >
              {ev.event_category.replace('_', ' ')}
            </span>
          )}
        </div>

        {/* Affected entities */}
        {detail && 'affected_entities' in detail && detail.affected_entities?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {detail.affected_entities.map((entity) => (
              <span
                key={entity.id}
                className="text-[9px] font-medium px-1.5 py-0.5 rounded"
                style={{ backgroundColor: 'var(--color-surface)', color: 'var(--color-text-secondary)' }}
              >
                {entity.entity_identifier}
              </span>
            ))}
          </div>
        )}

        {/* Impact */}
        {(ev.energy_impact_kwh != null || ev.capacity_affected_kw != null) && (
          <div className="flex items-center gap-3">
            {ev.energy_impact_kwh != null && (
              <div className="flex items-center gap-1.5">
                <Zap className="w-3 h-3" style={{ color: '#f59e0b' }} />
                <span className="text-[11px] eg-text-primary font-medium">
                  {ev.energy_impact_kwh.toLocaleString()} kWh
                </span>
              </div>
            )}
            {ev.capacity_affected_kw != null && (
              <div className="flex items-center gap-1.5">
                <Activity className="w-3 h-3" style={{ color: '#f97316' }} />
                <span className="text-[11px] eg-text-primary font-medium">
                  {ev.capacity_affected_kw.toLocaleString()} kW
                </span>
              </div>
            )}
          </div>
        )}

        {/* View in PDF button */}
        {meta?.source_page != null && onViewInPdf && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewInPdf(event.id);
            }}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-md border transition-colors"
            style={{
              borderColor: 'var(--color-border)',
              color: '#17c480',
              backgroundColor: 'transparent',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(23, 196, 128, 0.08)';
              (e.currentTarget as HTMLElement).style.borderColor = 'rgba(23, 196, 128, 0.4)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              (e.currentTarget as HTMLElement).style.borderColor = 'var(--color-border)';
            }}
          >
            <FileText className="w-3 h-3" />
            View in PDF — Page {meta.source_page}
          </button>
        )}
      </div>
    </div>
  );
};

// --- Event Group ---

interface EventGroupProps {
  eventType: string;
  events: SiteEvent[];
  siteId: string;
  expandedEventId: string | null;
  onToggleEvent: (eventId: string) => void;
  onViewInPdf?: (eventId: string) => void;
}

const EventGroup: React.FC<EventGroupProps> = ({ eventType, events, siteId, expandedEventId, onToggleEvent, onViewInPdf }) => {
  const [expanded, setExpanded] = useState(true);
  const config = EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG.other;

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
        style={{ backgroundColor: 'transparent' }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
        }}
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5 eg-text-muted flex-shrink-0" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 eg-text-muted flex-shrink-0" />
        )}
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: config.color }}
        />
        <span className="text-xs font-medium eg-text-primary">{config.label}</span>
        <span
          className="ml-auto text-[10px] font-medium rounded-full px-1.5 py-0.5"
          style={{
            backgroundColor: `${config.color}20`,
            color: config.color,
          }}
        >
          {events.length}
        </span>
      </button>

      <div
        style={{
          display: 'grid',
          gridTemplateRows: expanded ? '1fr' : '0fr',
          transition: 'grid-template-rows 200ms ease',
        }}
      >
        <div style={{ overflow: 'hidden' }}>
          <div className="pl-4 space-y-0.5 pb-1">
            {events.map((event) => {
              const statusStyle = STATUS_STYLES[event.status] || STATUS_STYLES.open;
              const isExpanded = expandedEventId === event.id;
              return (
                <div key={event.id}>
                  <button
                    onClick={() => onToggleEvent(event.id)}
                    className="w-full text-left px-3 py-2 rounded-lg transition-colors flex items-start gap-3 group"
                    style={{
                      backgroundColor: isExpanded ? 'var(--color-surface-active)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--color-surface-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) {
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: '#17c480' }} />
                    ) : (
                      <ChevronRight className="w-3 h-3 flex-shrink-0 mt-0.5 eg-text-muted group-hover:text-[#17c480] transition-colors" />
                    )}
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0 mt-1"
                      style={{ backgroundColor: SEVERITY_HEX_COLORS[event.severity] }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium eg-text-primary truncate flex-1">
                          {event.title}
                        </p>
                        <span
                          className="text-[9px] font-medium rounded px-1.5 py-0.5 flex-shrink-0 capitalize"
                          style={{
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.text,
                          }}
                        >
                          {formatStatus(event.status)}
                        </span>
                      </div>
                      {!isExpanded && event.description && (
                        <p className="text-[10px] eg-text-muted truncate mt-0.5">
                          {event.description.split('\n')[0]}
                        </p>
                      )}
                    </div>
                    <span className="text-[10px] eg-text-muted whitespace-nowrap flex-shrink-0 mt-0.5">
                      {timeAgo(event.occurred_at)}
                    </span>
                  </button>

                  {/* Inline detail expansion */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateRows: isExpanded ? '1fr' : '0fr',
                      transition: 'grid-template-rows 200ms ease',
                    }}
                  >
                    <div style={{ overflow: 'hidden' }}>
                      {isExpanded && (
                        <EventInlineDetail event={event} siteId={siteId} onViewInPdf={onViewInPdf} />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const EventExtractionPanel: React.FC<EventExtractionPanelProps> = ({ documentId, siteId, onEventClick }) => {
  const { data, isLoading, isError } = useGetDocumentEventsQuery(
    { documentId },
    { skip: !documentId }
  );

  const events = data || [];
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  const handleToggleEvent = (eventId: string) => {
    setExpandedEventId((prev) => (prev === eventId ? null : eventId));
  };

  const groupedEvents = useMemo(() => {
    if (!events.length) return [];

    const groups: Record<string, SiteEvent[]> = {};
    for (const event of events) {
      const type = event.event_type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(event);
    }

    // Sort events within each group by occurred_at desc
    for (const type of Object.keys(groups)) {
      groups[type].sort(
        (a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime()
      );
    }

    // Return groups ordered by the config order, then by count
    const configOrder = Object.keys(EVENT_TYPE_CONFIG);
    return Object.entries(groups).sort(([a], [b]) => {
      const ai = configOrder.indexOf(a);
      const bi = configOrder.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [events]);

  if (isLoading) {
    return (
      <div className="p-4 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 text-engramiq-green animate-spin" />
        <span className="text-xs eg-text-muted">Loading events...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 flex items-center justify-center gap-2">
        <AlertCircle className="w-4 h-4" style={{ color: '#ef4444' }} />
        <span className="text-xs eg-text-muted">Failed to load events</span>
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="p-4 text-center">
        <span className="text-xs eg-text-muted">No events found for this document</span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <Zap className="w-4 h-4 text-engramiq-green" />
        <span className="text-xs font-medium eg-text-secondary">
          {events.length} event{events.length !== 1 ? 's' : ''} extracted from this document
        </span>
      </div>

      <div className="space-y-1">
        {groupedEvents.map(([eventType, groupEvents]) => (
          <EventGroup
            key={eventType}
            eventType={eventType}
            events={groupEvents}
            siteId={siteId}
            expandedEventId={expandedEventId}
            onToggleEvent={handleToggleEvent}
            onViewInPdf={onEventClick}
          />
        ))}
      </div>
    </div>
  );
};

export default EventExtractionPanel;
